"use client"

import { useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import Sidebar from './sidebar'
import { getStoredUser, getStoredValue } from '../../utils/service'

interface MainLayoutProps {
  children: ReactNode
  activeSection?: string
  setActiveSection?: (section: string) => void
}

const MainLayout = ({ 
  children,
  activeSection = 'dashboard',
  setActiveSection
}: MainLayoutProps) => {
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)

  const storedUser = getStoredUser()
  const user = {
    name: storedUser ? `${storedUser.first_name} ${storedUser.last_name}` : localStorage.getItem('name') || 'Sales Admin',
    email: storedUser?.email || getStoredValue('email') || 'sales@proapp.me',
    role: storedUser?.role || localStorage.getItem('role') || 'Sales Manager',
    initials: storedUser && storedUser.first_name && storedUser.last_name 
      ? `${storedUser.first_name[0]}${storedUser.last_name[0]}`.toUpperCase() 
      : (localStorage.getItem('name') || 'SA').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth
      const mobile = width < 768
      const tablet = width >= 768 && width < 1024
      setIsMobile(mobile)
      setIsTablet(tablet)
      
      if (mobile || tablet) {
        setSidebarOpen(false)
      } else {
        setSidebarOpen(true)
      }
    }

    checkDevice()
    window.addEventListener('resize', checkDevice)
    return () => window.removeEventListener('resize', checkDevice)
  }, [])

  const toggleSidebar = (isOpen: boolean) => {
    setSidebarOpen(isOpen)
  }

  return (
    <div className={`h-screen overflow-hidden ${isMobile || isTablet ? 'bg-white' : 'bg-white'}`}>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      <Sidebar 
        isOpen={sidebarOpen} 
        toggleSidebar={toggleSidebar}
        userRole="admin"
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        isMobile={isMobile || isTablet}
        user={user}
        navigate={navigate}
      />
      
      <div className={`h-screen transition-all duration-300 ${
        isMobile || isTablet 
          ? 'pt-20 px-2 pb-2' 
          : sidebarOpen 
            ? 'ml-[21rem] p-4' 
            : 'ml-20 p-4'
      }`}>
        <main className={`h-full overflow-y-auto no-scrollbar ${
          isMobile || isTablet 
            ? 'bg-white rounded-lg' 
            : 'bg-white rounded-lg'
        }`}>
          {children}
        </main>
      </div>
    </div>
  )
}

export default MainLayout